import { mapMoney, numberOr } from "./money";
import { getApiErrorFieldCode } from "../../lib/api/apiError";

const INSUFFICIENT_FUNDS_CODE = "insufficient.funds";
const INVALID_ORDER_AMOUNT_CODE = "order.invalid.amount";

const CHECKOUT_URL = /^https:\/\/checkout\.paystack\.com\/([A-Za-z0-9._-]+)\/?$/i;

const accessCodeOf = (explicit, authorizationUrl) => {
  const given = typeof explicit === "string" ? explicit.trim() : "";

  if (given) return given;

  const match = CHECKOUT_URL.exec(String(authorizationUrl || "").trim());

  return match ? match[1] : null;
};

const SETTLED_CODES = ["COMPLETED", "CLAIMED", "SUCCESS", "SUCCESSFUL", "PAID"];
const FAILED_CODES = [
  "FAILED",
  "ABANDONED",
  "REVERSED",
  "CANCELLED",
  "CANCELED",
  "DECLINED",
  "EXPIRED",
];

export const mapPaymentMethod = (dto) => ({
  code: dto?.code ?? null,
  title: dto?.title || dto?.code || "",
  description: dto?.description ?? null,
  enabled: dto?.enabled !== false,
  balance: mapMoney(dto?.balance),
  disabledReason: dto?.disabledReason ?? null,
});

export const mapPaymentMethods = (list) => (list || []).map(mapPaymentMethod);

export const mapPaystackInitialization = (dto) => {
  const authorizationUrl = dto?.data?.authorization_url;
  const reference = dto?.data?.reference;

  if (!authorizationUrl || !reference) return null;

  return {
    authorizationUrl,
    accessCode: accessCodeOf(dto?.data?.access_code, authorizationUrl),
    reference,
  };
};

const statusCodeOf = (dto) => {
  const raw =
    dto?.transactionStatus ??
    dto?.paystackTransaction?.transactionStatus ??
    dto?.data?.transactionStatus ??
    dto?.data?.status ??
    (typeof dto?.status === "string" ? dto.status : null);

  if (!raw) return "UNKNOWN";

  const code = String(raw).toUpperCase();

  if (SETTLED_CODES.includes(code)) return "COMPLETED";
  if (FAILED_CODES.includes(code)) return "FAILED";

  return "PENDING";
};

export const mapPaystackStatus = (dto) => ({
  status: statusCodeOf(dto),
  reference: dto?.reference ?? dto?.paystackTransaction?.reference ?? dto?.data?.reference ?? null,
  message:
    dto?.message ?? dto?.paystackTransaction?.msg ?? dto?.data?.gateway_response ?? null,
  transactionId: dto?.transactionId ?? (typeof dto?.id === "number" ? dto.id : null),
});

export const isInsufficientFunds = (error) =>
  getApiErrorFieldCode(error) === INSUFFICIENT_FUNDS_CODE;

export const isStaleOrderAmount = (error) =>
  getApiErrorFieldCode(error) === INVALID_ORDER_AMOUNT_CODE;

export const mapTopUpInitialization = (dto) => {
  const authorizationUrl = dto?.authorizationUrl ?? dto?.data?.authorization_url;
  const reference = dto?.reference ?? dto?.data?.reference;

  if (!authorizationUrl || !reference) return null;

  return {
    authorizationUrl,
    accessCode: accessCodeOf(dto?.accessCode ?? dto?.data?.access_code, authorizationUrl),
    reference,
  };
};

export const mapCodTransaction = (dto) => {
  const body = dto?.transaction || dto?.data || dto;
  const id = body?.id;

  if (id === null || id === undefined) return null;

  return { id, reference: body?.reference ?? null, status: body?.status ?? null };
};

export const mapWallet = (dto) => {
  const id = numberOr(dto?.id);

  if (id === null) return null;

  const currencyCode = (dto?.currencyCode || "").toUpperCase() || null;

  return {
    id,
    currencyCode,
    balance: { value: numberOr(dto?.currentBalance) ?? 0, currency: currencyCode },
  };
};

export const mapFxQuote = (dto, args = {}) => {
  const rate = numberOr(dto?.rate);

  if (rate === null) return null;

  const from = (dto?.from || args.from || "").toUpperCase() || null;
  const to = (dto?.to || args.to || "").toUpperCase() || null;
  const sourceAmount = numberOr(dto?.sourceAmount) ?? numberOr(args.amount) ?? 1;
  const convertedAmount = numberOr(dto?.convertedAmount) ?? sourceAmount * rate;

  return {
    from,
    to,
    rate,
    sourceAmount: { value: sourceAmount, currency: from },
    convertedAmount: { value: convertedAmount, currency: to },
  };
};
